import { AppBar, Tabs, Tab, Button } from '@mui/material';
import { useQuery } from 'react-query';
import makeStyles from '@mui/styles/makeStyles';
import { FC, useEffect, useState, useMemo, useContext } from 'react';
import { UserContext } from '../../../../context';
import { useHistory, useParams } from 'react-router-dom';
import { Page, PageTitleWithButton, Button as SMButton } from '../../../../components';
import {
  getUnderContractAddressBook,
  getUnderContractDates,
  getUnderContractDocuments,
  getContractDetails
} from '../../../../fetch';
import { IContractDetails, EmailType, IUnderContractDocumentResults } from '../../../../models';
import { ContractDates } from './contract-dates/under-contract-contract-dates';
import { ContractFiles } from './contract-files/under-contract-files';
import { ClientHistory } from './client-history/client-history';
import { Email, Info } from '@mui/icons-material';
import { UnderContractEmailModal } from './send-email/under-contract-email-modal';
import { AddressBook } from './address-book';
import {
  getSecuritySensitiveRandomNumber,
  isAllowedToEditUnderContract
} from '../../../../helpers';
import { useFlags } from '../../../../hooks';

export const enum underContractPages {
  contractDates,
  contractFiles,
  clientHistory,
  addressBook
}

export const UnderContractDetails: FC = () => {
  const classes = useStyles();
  const userContext = useContext(UserContext);
  const { documentationBuildDocumentation } = useFlags();
  const hasEditAccess = isAllowedToEditUnderContract(userContext.user);
  const [selectedPage, setSelectedPage] = useState<underContractPages>(
    underContractPages.contractDates
  );
  const [selectedTab, setSelectedTab] = useState(window.location.pathname);
  const [isDirtyForm, setDirtyForm] = useState<boolean>(false);
  const { buildId }: { buildId: string } = useParams();
  const history = useHistory();

  const {
    isLoading: isLoadingContractDetails,
    data: contractDetails,
    refetch: refetchContractDetails
  } = useQuery<IContractDetails, Error>(
    ['getContractDetails'],
    async () => await getContractDetails(buildId),
    {
      enabled: !!buildId
    }
  );

  const {
    isLoading: isLoadingContractDates,
    data: contractDates,
    refetch: refetchContractDates
  } = useQuery<any, Error>(
    ['contract-dates', buildId],
    async () => await getUnderContractDates(buildId),
    {
      enabled: !!buildId
    }
  );

  const {
    isLoading: isLoadingContractFiles,
    data: contractDocumentResults,
    refetch: refetchContractDocumentResults
  } = useQuery<IUnderContractDocumentResults, Error>(
    ['under-contract-documents-data', buildId],
    async () => await getUnderContractDocuments(buildId),
    {
      notifyOnChangeProps: 'tracked',
      enabled: !!buildId
    }
  );

  const {
    isLoading: isLoadingAddressBook,
    data: addressBook,
    refetch: refetchAddressBook,
    isRefetching: isRefetchingAddressBook
  } = useQuery<any, Error>(
    ['address-book', buildId],
    async () => await getUnderContractAddressBook(buildId),
    {
      enabled: !!buildId
    }
  );

  const [emailSentRef, setEmailSentRef] = useState(getSecuritySensitiveRandomNumber() * 1000);
  const handleEmailSentRef = () => {
    setEmailSentRef(getSecuritySensitiveRandomNumber() * 1000);
  };

  useEffect(() => {
    if (window.location.href.includes('contract-dates')) {
      setSelectedPage(underContractPages.contractDates);
    }
    if (window.location.href.includes('contract-files')) {
      setSelectedPage(underContractPages.contractFiles);
    }
    if (window.location.href.includes('contact-history')) {
      setSelectedPage(underContractPages.clientHistory);
    }
    if (window.location.href.includes('address-book')) {
      setSelectedPage(underContractPages.addressBook);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location.href, buildId]);

  const getBuildId = () => {
    return buildId ? buildId : 'edit';
  };
  const contractLinks = useMemo(
    () =>
      [
        {
          name: 'Contract Dates',
          url: `/sales/contracting/under-contract/${getBuildId()}/contract-dates`
        },
        {
          name: 'Contract Files',
          url: `/sales/contracting/under-contract/${getBuildId()}/contract-files`
        },
        {
          name: 'Contact History',
          url: `/sales/contracting/under-contract/${getBuildId()}/contact-history`
        },
        {
          name: 'Address Book',
          url: `/sales/contracting/under-contract/${getBuildId()}/address-book`
        }
      ].filter(Boolean),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [buildId]
  );

  const showLoader = isLoadingContractDetails;
  const title = showLoader
    ? 'Loading...'
    : contractDetails?.contractName
    ? contractDetails.contractName
    : 'Edit Contract Information';

  const [emailType, setEmailType] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setisLoading] = useState(false);
  return (
    <Page pageHasStickyHeaders title={title}>
      <>
        <PageTitleWithButton
          pageTitleBackProps={{
            title: title,
            backText: 'Back to Under Contracts',
            backUrl: '/sales/contracting/under-contract'
          }}
          button={
            <>
              {documentationBuildDocumentation && (
                <SMButton
                  startIcon={<Info />}
                  id="build-documentation-details"
                  color="secondary"
                  variant="contained"
                  asLink
                  to={`/documentation/build-documentation/${buildId}`}
                >
                  Build Documentation
                </SMButton>
              )}
              <SMButton
                startIcon={<Info />}
                id="contract-acceptance-details"
                color="secondary"
                variant="contained"
                asLink
                to={`/sales/contracting/contract-acceptance/${buildId}`}
              >
                Contract Details
              </SMButton>
              <Button
                disabled={!hasEditAccess}
                startIcon={<Email />}
                onClick={() => {
                  setEmailType(EmailType.Email);
                  setIsModalOpen(true);
                }}
                color="primary"
                variant="contained"
              >
                Email
              </Button>
            </>
          }
        />
        {buildId !== 'add' && (
          <AppBar position="static" color="default" component="div" className={classes.tabBar}>
            <Tabs
              value={selectedTab}
              indicatorColor="primary"
              textColor="primary"
              allowScrollButtonsMobile
              onChange={(_, value) => {
                if (!isDirtyForm) {
                  setSelectedTab(value);
                  history.push(value);
                } else {
                  const result = window.confirm(
                    'You have unsaved changes, are you sure you want to exit?'
                  );
                  if (result) {
                    setSelectedTab(value);
                    history.push(value);
                  } else {
                    return;
                  }
                }
              }}
              variant="scrollable"
            >
              {contractLinks.map(link => {
                return (
                  <Tab
                    value={link.url}
                    key={link.name}
                    id={`${link.name}-information`}
                    aria-controls={`${link.name}-information`}
                    label={link.name}
                  />
                );
              })}
            </Tabs>
          </AppBar>
        )}
        {selectedPage === underContractPages.contractDates && (
          <ContractDates
            contractDates={contractDates}
            isLoadingContractDates={isLoadingContractDates}
            setDirtyForm={setDirtyForm}
            refetchContractDates={refetchContractDates}
            buildId={buildId}
            contractDetails={contractDetails}
            selectedPage={selectedPage}
            handleEmailSentRef={handleEmailSentRef}
            refetchContractDetails={refetchContractDetails}
          />
        )}
        {selectedPage === underContractPages.contractFiles && (
          <ContractFiles
            contractDocuments={contractDocumentResults}
            isLoadingContractFiles={isLoadingContractFiles}
            buildId={buildId}
            refetchContractDocumentResults={refetchContractDocumentResults}
          />
        )}
        {selectedPage === underContractPages.clientHistory && (
          <ClientHistory buildId={buildId} emailSentRef={emailSentRef} />
        )}
        {selectedPage === underContractPages.addressBook && (
          <AddressBook
            buildId={buildId}
            isLoading={isLoadingAddressBook || isRefetchingAddressBook}
            addressBook={addressBook}
            refetchAddressBook={refetchAddressBook}
          />
        )}
      </>
      <UnderContractEmailModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        setIsLoading={() => setisLoading(false)}
        isLoading={isLoading}
        buildId={buildId}
        selectedPage={selectedPage}
        fetchClientHistory={handleEmailSentRef}
        emailType={emailType}
        contractDates={contractDates}
        refetchContractDetails={refetchContractDetails}
      />
    </Page>
  );
};

const useStyles = makeStyles(theme => ({
  tabBar: {
    marginTop: '-1rem',
    backgroundColor: '#fff',
    boxShadow: 'none',
    borderBottom: `1px solid ${theme.palette.grey[400]}`
  },
  inviteButton: {
    marginRight: '5px'
  }
}));
